import { useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import {
  compareMrpForecastStackOrder,
  formatNumber,
  mrpLocationChipStyles,
  type MrpInventoryRow,
  type MrpLocation,
} from "@/lib/mrp";
import { cn } from "@/lib/utils";

function forecastLocationKey(code: string, forecastLocationCodes: readonly string[]): string | null {
  const normalized = code.trim().toLowerCase();
  return forecastLocationCodes.find((entry) => entry.trim().toLowerCase() === normalized) ?? null;
}

export function MrpOnHandPeekHoverHintBody({
  cpn,
  forecastLocationCodes,
}: {
  cpn: string;
  forecastLocationCodes: readonly string[];
}): JSX.Element {
  const inventoryQuery = useQuery<{ rows: MrpInventoryRow[] }, ApiError>({
    queryKey: ["mrp", "inventory", "peek", cpn, [...forecastLocationCodes].sort()],
    queryFn: () =>
      apiFetch<{ rows: MrpInventoryRow[] }>("/api/mrp/inventory", {
        query: { cpn },
      }),
    enabled: forecastLocationCodes.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (forecastLocationCodes.length === 0) {
    return <span className="text-slate-600">No forecast locations configured.</span>;
  }

  if (inventoryQuery.isLoading) {
    return <span className="text-slate-600">Loading on hand…</span>;
  }

  if (inventoryQuery.isError) {
    return <span className="text-red-700">{inventoryQuery.error.message}</span>;
  }

  const byLocation = new Map<string, number>();
  for (const locationCode of forecastLocationCodes) {
    byLocation.set(locationCode, 0);
  }
  for (const row of inventoryQuery.data?.rows ?? []) {
    const locationCode = forecastLocationKey(row.location_code, forecastLocationCodes);
    if (locationCode == null) continue;
    byLocation.set(locationCode, (byLocation.get(locationCode) ?? 0) + (row.quantity ?? 0));
  }

  const sorted = [...byLocation.entries()].sort(([left], [right]) =>
    compareMrpForecastStackOrder(left, right),
  );
  const total = sorted.reduce((sum, [, quantity]) => sum + quantity, 0);

  return (
    <div className="w-max max-w-[16rem] space-y-1.5">
      <div className="border-b border-slate-100 pb-1 text-xs font-medium text-slate-800">
        On hand for {cpn}
      </div>
      <ul className="space-y-1 text-[11px] leading-snug text-slate-700">
        {sorted.map(([locationCode, quantity]) => {
          const chip = mrpLocationChipStyles(locationCode as MrpLocation);
          return (
            <li key={locationCode} className="flex items-center justify-between gap-3">
              <span
                className={cn(
                  "inline-flex rounded border px-1.5 py-0 text-[10px] font-medium",
                  chip.className,
                )}
              >
                {chip.label}
              </span>
              <span className="tabular-nums text-slate-900">{formatNumber(quantity)}</span>
            </li>
          );
        })}
      </ul>
      {sorted.length > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-1 text-[11px] font-medium text-slate-800">
          <span>Total</span>
          <span className="tabular-nums">{formatNumber(total)}</span>
        </div>
      ) : null}
    </div>
  );
}
